import { useCallback, useState } from "react";

/**
 * @template T
 * @param {string} key
 * @param {T} initialValue
 * @param {{ writeDefault?: boolean, persist?: boolean }} [options]
 * @returns {[T, (value: T | ((prev: T) => T)) => void]}
 */
export default function useLocalStorage(key, initialValue, options = {}) {
  const { writeDefault = false, persist = true } = options;
  const [storedValue, setStoredValue] = useState(() => {
    if (!persist) {
      return initialValue;
    }
    try {
      const item = localStorage.getItem(key);
      if (item != null) {
        return JSON.parse(item);
      }
      if (writeDefault) {
        localStorage.setItem(key, JSON.stringify(initialValue));
      }
      return initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value) => {
      setStoredValue((prev) => {
        const nextValue = value instanceof Function ? value(prev) : value;
        if (!persist) {
          return nextValue;
        }
        try {
          if (nextValue === undefined || nextValue === null) {
            localStorage.removeItem(key);
          } else {
            localStorage.setItem(key, JSON.stringify(nextValue));
          }
        } catch {}
        return nextValue;
      });
    },
    [key, persist],
  );

  return [storedValue, setValue];
}
